import { useState } from "react";
import { MapPin, Phone, Instagram, Clock } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { motion } from "framer-motion";

// TikTok SVG icon
const TikTokIcon = ({ className = "h-4 w-4", ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 16 16"
    fill="currentColor"
    className={className}
    aria-hidden="true"
    {...props}
  >
    <path d="M9 0h1.5c.1 1.2.7 2.2 1.6 3.1.9.9 1.9 1.4 3 1.6V9c-1.8 0-3.4-.6-4.6-1.7V11c0 3-2.1 5-5.1 5C2.7 16 0 13.8 0 10.8 0 7.9 2.3 5.6 5.2 5.6c.4 0 .8.1 1.2.2v2.7c-.4-.1-.7-.2-1.1-.2-1.3 0-2.4 1.1-2.4 2.5 0 1.5 1.1 2.6 2.6 2.6 1.5 0 2.5-1 2.5-2.8V0z" />
  </svg>
);

const ContactSection = ({ translations }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success(translations.contact.form.success, {
      description: translations.contact.form.successDescription,
    });
    setFormData({ name: "", email: "", message: "" });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: translations.contact.info.address,
      content: "Rheinstraße 32–33, 12161 Berlin",
      gradient: "linear-gradient(135deg, #E91E63, #F06292)",
    },
    {
      icon: Phone,
      title: translations.contact.info.phone,
      content: "+493027692927",
      gradient: "linear-gradient(135deg, #FF6F00, #FFA726)",
    },
    {
      icon: Clock,
      title: translations.contact.info.hours,
      content: translations.contact.info.hoursValue,
      gradient: "linear-gradient(135deg, #FFA726, #FFB74D)",
    },
    {
      icon: Instagram,
      title: "Instagram",
      content: "@bosnabaeckerei",
      link: "https://instagram.com/bosnabaeckerei",
      gradient: "linear-gradient(135deg, #9C27B0, #E91E63)",
    },
    {
      icon: TikTokIcon,
      title: "TikTok",
      content: "@bosnabaeckerei",
      link: "https://www.tiktok.com/@bosnabaeckerei",
      gradient: "linear-gradient(135deg, #111827, #374151)",
    },
  ];

  return (
    <section
      className="min-h-screen w-full py-12 md:py-16 px-4 md:px-6 pb-24 md:pb-16"
      style={{ background: "linear-gradient(to bottom, #FFFFFF 0%, #F8F9FA 100%)" }}
    >
      <div className="container mx-auto max-w-6xl">
        <div className="text-center mb-12">
          <motion.h2
            initial={{ opacity: 0, rotate: -5 }}
            whileInView={{ opacity: 1, rotate: 0 }}
            className="text-3xl md:text-5xl lg:text-6xl font-bold mb-4"
            style={{
              background: "linear-gradient(135deg, #731919, #E52B2B, #FF6B6B)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {translations.contact.title}
          </motion.h2>

          <p className="text-muted-foreground">
            {translations.contact.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="space-y-4">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <Card key={index} className="bg-white hover:shadow-xl transition">
                  <CardHeader className="p-4">
                    <div className="flex items-center gap-3">
                      <div
                        className="p-3 rounded-xl shadow-lg"
                        style={{ background: info.gradient }}
                      >
                        <Icon className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <CardTitle style={{ color: "#731919" }}>
                          {info.title}
                        </CardTitle>
                        <CardDescription>
                          {info.link ? (
                            <a
                              href={info.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-red-600"
                            >
                              {info.content}
                            </a>
                          ) : (
                            info.content
                          )}
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              );
            })}
          </div>

          <Card className="bg-white shadow-xl">
            <CardHeader>
              <CardTitle style={{ color: "#731919" }}>
                {translations.contact.form.title}
              </CardTitle>
              <CardDescription>
                {translations.contact.form.description}
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  name="name"
                  placeholder={translations.contact.form.name}
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
                <Input
                  name="email"
                  type="email"
                  placeholder={translations.contact.form.email}
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
                <Textarea
                  name="message"
                  placeholder={translations.contact.form.message}
                  value={formData.message}
                  onChange={handleChange}
                  required
                />
                <Button
                  type="submit"
                  className="w-full text-white"
                  style={{ background: "linear-gradient(135deg, #731919, #E52B2B)" }}
                >
                  {translations.contact.form.submit}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
